import { apiClient } from "@/lib/api/client";

export type AnnouncementAudience = { target_type: "all" | "platform" | "customers" | "workspace" | "user"; target_id?: string | null; target_role?: string | null };
export type AnnouncementReadSummary = { recipient_count: number; read_count: number; unread_count: number; read_rate: number };
export type Announcement = { id: string; title: string; content_html: string; announcement_type: string; status: string; priority: string; delivery_channels: string[]; publish_at: string | null; expires_at: string | null; created_by_user_id: string; created_at: string; updated_at: string; audiences: AnnouncementAudience[]; is_read: boolean; read_summary: AnnouncementReadSummary | null };
export type AnnouncementList = { items: Announcement[]; total: number; unread_count: number };
export type AnnouncementAudienceMember = { id: string; name: string | null; email: string; role: string };
export type AnnouncementReader = { id: string; name: string | null; email: string; account_type: string; role: string; read_at: string };
export type AnnouncementReadAnalytics = { recipient_count: number; read_count: number; unread_count: number; read_rate: number; readers: AnnouncementReader[] };
export type AnnouncementInput = Omit<Announcement, "id" | "created_by_user_id" | "created_at" | "updated_at" | "is_read" | "audiences"> & { audiences: AnnouncementAudience[] };

export type AnnouncementListOptions = {
  search?: string;
  published?: "all" | "published" | "draft";
  priority?: "all" | "normal" | "important" | "urgent";
  limit?: number;
  offset?: number;
};

export async function listAnnouncements(scope: "audience" | "management" = "audience", options: AnnouncementListOptions = {}): Promise<AnnouncementList> {
  const params: Record<string, string | number | boolean> = { scope };
  if (options.search?.trim()) params.search = options.search.trim();
  if (options.published === "published") params.published = true;
  if (options.published === "draft") params.published = false;
  if (options.priority && options.priority !== "all") params.priority = options.priority;
  if (options.limit) params.limit = options.limit;
  if (typeof options.offset === "number") params.offset = options.offset;
  const { data } = await apiClient.get<AnnouncementList>("/announcements", { params });
  return data;
}
export async function getAnnouncement(id: string, scope: "audience" | "management" = "audience"): Promise<Announcement> {
  const { data } = await apiClient.get<Announcement>(`/announcements/${id}`, { params: { scope } });
  return data;
}
export async function createAnnouncement(body: AnnouncementInput): Promise<Announcement> {
  const { data } = await apiClient.post<Announcement>("/announcements", body);
  return data;
}
export async function updateAnnouncement(id: string, body: AnnouncementInput): Promise<Announcement> {
  const { data } = await apiClient.put<Announcement>(`/announcements/${id}`, body);
  return data;
}
export async function markAnnouncementRead(id: string): Promise<Announcement> {
  const { data } = await apiClient.post<Announcement>(`/announcements/${id}/read`);
  return data;
}

export async function setAnnouncementPublication(id: string, published: boolean): Promise<Announcement> {
  const { data } = await apiClient.patch<Announcement>(`/announcements/${id}/publication`, { published });
  return data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await apiClient.delete(`/announcements/${id}`);
}

export async function listAnnouncementAudienceMembers(tenantId: string): Promise<AnnouncementAudienceMember[]> {
  const { data } = await apiClient.get<AnnouncementAudienceMember[]>("/announcements/audience-members", {
    params: { tenant_id: tenantId },
  });
  return data;
}

export async function getAnnouncementReadAnalytics(id: string): Promise<AnnouncementReadAnalytics> {
  const { data } = await apiClient.get<AnnouncementReadAnalytics>(`/announcements/${id}/read-analytics`);
  return data;
}
